import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CheckCircle, User, Briefcase, DollarSign, Ruler, FileText, Globe, Star, Shield, Home } from "lucide-react";
import {
  HomeIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChartBarIcon,
  BanknotesIcon
} from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import SEO from '../SEO/SEO';

const HomeCompositeLoan = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [activeTab, setActiveTab] = useState('common');
  const [activeFAQ, setActiveFAQ] = useState(0);  // OPEN FIRST FAQ BY DEFAULT
  const [activeCalc, setActiveCalc] = useState("overview");

  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fallback static banner data
  const fallbackBanner = {
    title: 'Turn Your Plot into a Home with Aham Composite Loan',
    subtitle: 'One loan for plot purchase and home construction, designed for a seamless and worry-free building journey.',
    image_url: 'img/loan-banners/Composite-Loan-Banner.webp',
    primary_button_text: 'Apply Now',
    primary_button_link: 'https://aham.mbwhost.in/contact',
    highlights: []
  };

  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Fallback static overview data
  const fallbackOverview = {
    title: 'Composite Loan (Plot Purchase + Construction)',
    description: `At Aham Housing Finance, we understand that building your dream home begins with choosing the perfect plot and transforming it into a space you can proudly call your own. Our Composite Loan helps you finance both the purchase of a residential plot and the construction of your home — all under a single loan.

With simplified documentation, flexible tenure options, and stage-wise disbursement, our composite loan ensures a smooth and stress-free experience throughout your home-building journey.`,
    features: [
      'Single loan for plot + construction – No need to manage multiple loans or repayment schedules.',
      'Flexible repayment up to 20 years – Choose tenure that suits your financial plan.',
      'Stage-wise disbursement – Loan released as per construction progress for transparent fund usage.',
      'Easy documentation & faster processing – Combined process for both land and construction approval.'
    ],
    image_url: '/img/loan-overview/Composite-Loan-Overview.webp'
  };

  // Key Benefits state
  const [benefits, setBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState(null);
  const [benefitsLoading, setBenefitsLoading] = useState(true);

  // Fallback benefits data
  const fallbackBenefits = [
    {
      id: 1,
      title: 'Loan up to ₹25 Lakhs',
      description: 'Get substantial funding to cover both plot purchase and construction costs.',
      icon_name: 'BanknotesIcon',
      gradient_from: 'blue-500',
      gradient_to: 'indigo-600'
    },
    {
      id: 2,
      title: 'Up to 20 Years Tenure',
      description: 'Repay comfortably with flexible tenure options tailored to your income.',
      icon_name: 'ClockIcon',
      gradient_from: 'emerald-500',
      gradient_to: 'teal-600'
    },
    {
      id: 3,
      title: 'Collateral Free Loans',
      description: 'No additional collateral required - your plot and future home serve as security.',
      icon_name: 'ShieldCheckIcon',
      gradient_from: 'indigo-500',
      gradient_to: 'purple-600'
    },
    {
      id: 4,
      title: 'Minimal Documentation',
      description: 'Simple and hassle-free documentation process for faster approval.',
      icon_name: 'DocumentTextIcon',
      gradient_from: 'orange-500',
      gradient_to: 'red-600'
    },
    {
      id: 5,
      title: 'Tax Benefits',
      description: 'Avail tax deductions on both principal and interest repayment under IT Act.',
      icon_name: 'ChartBarIcon',
      gradient_from: 'pink-500',
      gradient_to: 'rose-600'
    },
    {
      id: 6,
      title: 'Balance Transfer Facility',
      description: 'Transfer your existing loan to us for better interest rates and terms.',
      icon_name: 'CurrencyRupeeIcon',
      gradient_from: 'cyan-500',
      gradient_to: 'blue-600'
    }
  ];

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
  };



  // Eligibility Criteria
  // Eligibility state
  const [eligibility, setEligibility] = useState([]);
  const [eligibilitySettings, setEligibilitySettings] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);

  // Fetch dynamic eligibility and settings
  useEffect(() => {
    const fetchEligibilityData = async () => {
      try {
        setEligibilityLoading(true);
        const [criteriaRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility/composite_loan/active'),
          axios.get('/api/admin/eligibility/settings/composite_loan')
        ]);

        if (criteriaRes.data.data) {
          setEligibility(criteriaRes.data.data);
        }
        if (settingsRes.data.data) {
          setEligibilitySettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility:', error);
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchEligibilityData();
  }, []);


  // Documents Required
  // Documents state
  const [documents, setDocuments] = useState({});
  const [documentsSettings, setDocumentsSettings] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Fetch dynamic documents and settings
  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        setDocumentsLoading(true);
        const [docsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/documents/composite_loan/active'),
          axios.get('/api/admin/documents/settings/composite_loan')
        ]);

        if (docsRes.data.data) {
          // Group documents by category
          const groupedDocs = docsRes.data.data.reduce((acc, doc) => {
            if (!acc[doc.category]) acc[doc.category] = [];
            acc[doc.category].push(doc.document_name);
            return acc;
          }, {});

          // Ensure all categories exist to prevent undefined errors
          const categories = ['common', 'salaried', 'selfEmployed', 'nri', 'property'];
          categories.forEach(cat => {
            if (!groupedDocs[cat]) groupedDocs[cat] = [];
          });

          setDocuments(groupedDocs);
        }

        if (settingsRes.data.data) {
          setDocumentsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocumentsData();
  }, []);


  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };


  // Fetch banner data on component mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/admin/banners/page/composite_loan');
        const banners = response.data.data || [];

        if (banners.length > 0) {
          // Use first active banner for this page
          setBanner(banners[0]);
        } else {
          // Use fallback if no banner found
          setBanner(fallbackBanner);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
        // Use fallback on error
        setBanner(fallbackBanner);
      } finally {
        setBannerLoading(false);
      }
    };

    fetchBanner();
  }, []);

  // Fetch overview data on component mount
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setOverviewLoading(true);
        const response = await axios.get('/api/admin/loan-overview/composite_loan');
        const data = response.data.data || null;

        if (data) {
          // Parse features if it's a string
          const parsedData = {
            ...data,
            features: typeof data.features === 'string' ? JSON.parse(data.features) : data.features
          };
          setOverview(parsedData);
        } else {
          // Use fallback if no overview found
          setOverview(fallbackOverview);
        }
      } catch (error) {
        console.error('Error fetching overview:', error);
        // Use fallback on error
        setOverview(fallbackOverview);
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverview();
  }, []);

  // Fetch key benefits data
  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        setBenefitsLoading(true);
        const [benefitsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/key-benefits/composite_loan/active'),
          axios.get('/api/admin/key-benefits/settings/composite_loan')
        ]);

        const fetchedBenefits = benefitsRes.data.data || [];

        if (fetchedBenefits.length > 0) {
          setBenefits(fetchedBenefits);
        } else {
          setBenefits(fallbackBenefits);
        }

        if (settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching benefits:', error);
        setBenefits(fallbackBenefits);
      } finally {
        setBenefitsLoading(false);
      }
    };

    fetchBenefits();
  }, []);

  // Interest Rates state
  const [interestRates, setInterestRates] = useState([]);
  const [interestRatesSettings, setInterestRatesSettings] = useState(null);
  const [interestRatesLoading, setInterestRatesLoading] = useState(true);

  // Fetch Interest Rates
  useEffect(() => {
    const fetchInterestRatesData = async () => {
      try {
        setInterestRatesLoading(true);
        const [cardsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/interest-rates/composite_loan/active'),
          axios.get('/api/admin/interest-rates/settings/composite_loan')
        ]);

        if (cardsRes.data.data) {
          setInterestRates(cardsRes.data.data);
        }

        if (settingsRes.data.data) {
          setInterestRatesSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching interest rates:', error);
      } finally {
        setInterestRatesLoading(false);
      }
    };

    fetchInterestRatesData();
  }, []);

  // FAQs state
  const [faqs, setFaqs] = useState([]);
  const [faqSettings, setFaqSettings] = useState(null);
  const [faqsLoading, setFaqsLoading] = useState(true);

  // Fetch FAQs
  useEffect(() => {
    const fetchFAQsData = async () => {
      try {
        setFaqsLoading(true);
        const [faqsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/faqs/composite_loan/active'),
          axios.get('/api/admin/faqs/settings/composite_loan')
        ]);

        if (faqsRes.data.data) {
          setFaqs(faqsRes.data.data);
        }

        if (settingsRes.data.data) {
          setFaqSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setFaqsLoading(false);
      }
    };

    fetchFAQsData();
  }, []);

  // CTA Banner state
  const [ctaSettings, setCtaSettings] = useState(null);
  const [ctaLoading, setCtaLoading] = useState(true);

  // Fetch CTA Banner
  useEffect(() => {
    const fetchCtaData = async () => {
      try {
        setCtaLoading(true);
        const response = await axios.get('/api/admin/cta-banner/settings/composite_loan');
        if (response.data.data) {
          setCtaSettings(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CTA banner:', error);
      } finally {
        setCtaLoading(false);
      }
    };

    fetchCtaData();
  }, []);

  // Scroll Event Listener to auto-switch active tab
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "benefits", "eligibility", "documents", "interest", "faq"];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveCalc(sec);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const headerHeight = 120; // top navbar + sticky navbar height

    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;

    const offsetPosition = elementPosition - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

            
            
  // Schema for SEO
  const compositeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "image": "https://ahamhfc.com/img/loan-banners/Slide-02-1770722226693-126687268.webp?t=1770802805000",
        "description": "AHAM HOUSING FINANCE LIMITED offers a wide range of home loans across various segments and diverse regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#webpage",
        "url": "https://ahamhfc.com/composite-home-loan-plot-construction",
        "name": "Composite Loan (Plot Purchase + Construction) - Aham Housing Finance",
        "description": "Apply for a composite home loan from Aham Housing Finance to finance plot purchase and home construction with a single loan solution.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#loan"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#primaryimage",
          "contentUrl": "https://ahamhfc.com/img/loan-overview/Composite-Loan-Overview-1770783333714-64626934.webp"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        }
      },
      {
        "@type": "LoanOrCredit",
        "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#loan",
        "name": "Composite Loan",
        "alternateName": "Plot Purchase + Construction Loan",
        "url": "https://ahamhfc.com/composite-home-loan-plot-construction",
        "image": {
          "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#primaryimage"
        },
        "description": "Single loan solution for plot purchase and home construction, designed to avoid managing multiple loans or repayment schedules.",
        "category": "Housing Finance",
        "loanType": "Composite Home Loan",
        "provider": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "audience": {
          "@type": "Audience",
          "audienceType": "Plot buyers and home construction borrowers"
        },
        "areaServed": {
          "@type": "Country",
          "name": "India"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/composite-home-loan-plot-construction/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Composite Loan",
            "item": "https://ahamhfc.com/composite-home-loan-plot-construction"
          }
        ]
      }
    ]
  };


  return (
    <div className="min-h-screen bg-white">


      <SEO
        title="Composite Loan for Plot & Construction | Aham Housing Finance"
        description="Apply for a composite home loan for plot purchase & construction at attractive rates. Calculate EMIs & check tax benefits. Start building with Aham today!"
        keywords="Aham home composite loan, Aham Housing Finance composite loan, home composite loan, composite home loan, plot plus construction loan, plot purchase and construction loan, land and construction loan, composite loan for house construction, home loan for plot and construction, housing composite loan, affordable composite loan, low documentation composite loan, no income proof composite loan, stage wise disbursement loan, flexible tenure composite loan, self employed composite loan, plot purchase loan with construction finance, composite housing finance India, Aham plot and construction loan, Aham Housing Finance plot loan"
        canonical="https://ahamhfc.com/composite-home-loan-plot-construction"
        ogTitle="Composite Loan for Plot & Construction | Aham Housing Finance"
        ogDescription="Apply for a composite home loan for plot purchase & construction at attractive rates. Calculate EMIs & check tax benefits. Start building with Aham today!"
       schema={compositeSchema}
           />

      {/* Hero Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {bannerLoading ? (
          // Loading state
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center w-full">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url || fallbackBanner.image_url})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
             

                <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || fallbackBanner.title}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || fallbackBanner.subtitle}
                </p>

                {/* Display highlights if available */}
                {banner?.highlights && banner.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-4 mb-8">
                    {banner.highlights.map((highlight, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 shadow-lg"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-white text-xs md:text-sm font-medium">{highlight}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  {banner?.primary_button_text && (
                    <a href={banner.primary_button_link || 'https://aham.mbwhost.in/contact'} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0"
                      >
                        {banner.primary_button_text}
                      </motion.button>
                    </a>
                  )}

                  {banner?.secondary_button_text && (
                    <a href={banner.secondary_button_link || '#'} className="w-full sm:w-auto text-left">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-primary-600 text-white hover:bg-primary-700 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto md:mx-0 border border-white/20"
                      >
                        {banner.secondary_button_text}
                      </motion.button>
                    </a>
                  )}
                </div>
                
                   {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Composite Loan</span>
                </motion.div>
                
              </motion.div>
            </div>
          </>
        )}
      </section>


      {/* Sticky Finance Navigation */}
      <div className="hidden md:block w-full py-3 sticky top-[80px] z-50">
        <div className="container mx-auto px-1">

          <div className="w-full bg-white rounded-full shadow-lg flex flex-wrap justify-center gap-2 md:gap-6 py-3 px-4">

            {[
              { id: "overview", label: "Overview" },
              { id: "benefits", label: "Benefits" },
              { id: "eligibility", label: "Loan Eligibility Criteria" },
              { id: "documents", label: "Documents" },
              { id: "interest", label: "Interest" },
              { id: "faq", label: "FAQ" }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveCalc(tab.id);
                  scrollToSection(tab.id); // ONLY this — smooth scroll with offset
                }}
                className={`
    px-6 py-2 rounded-full font-semibold transition-all 
    ${activeCalc === tab.id
                    ? "bg-yellow-400 text-blue-800 shadow-lg scale-105"
                    : "text-gray-500 hover:text-blue-700"
                  }
  `}
              >
                {tab.label}
              </button>

            ))}
          </div>
        </div>
      </div>


      {/* Overview Section */}
       <section id="overview" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center md:text-left">
                {overview?.title || 'Composite Loan'}
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                {overview?.description.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {overview?.features && overview.features.length > 0 && (
                  <ul className="space-y-3 mt-6">
                    {overview.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircleIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={overview?.image_url || 'img/loan-overview/Composite-Loan-Overview.webp'}
                alt="Aham Housing Finance expert discussing composite loan benefits for plot purchase and construction with a happy family"

                className="rounded-2xl shadow-large w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>






      {/* Key Benefits Section */}
      <section id="benefits" className="relative py-24 overflow-hidden">

        {/* 🔥 Background – gradient + subtle pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white -z-10" />

        <div className="container mx-auto px-4 relative z-10">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16 px-4"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight drop-shadow-sm leading-tight">
              {benefitsSettings?.section_title || 'Key Benefits & Features'}
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium">
              {benefitsSettings?.section_subtitle || 'Discover why Aham Composite Loan is the right choice for building your dream home'}
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {benefits.map((benefit, index) => {
              // Icon map to dynamically select icons based on icon_name
              const iconMap = {
                BanknotesIcon,
                ClockIcon,
                ShieldCheckIcon,
                DocumentTextIcon,
                ChartBarIcon,
                CurrencyRupeeIcon,
                HomeIcon,
                User,
                Briefcase,
                DollarSign,
                Ruler,
                FileText,
                Globe,
                Star,
                Shield,
                CheckCircle
              };

              const IconComponent = iconMap[benefit.icon_name] || Star;
              const gradientFrom = benefit.gradient_from || 'blue-500';
              const gradientTo = benefit.gradient_to || 'indigo-600';

              return (
                <motion.div
                  key={benefit.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  className="group relative bg-white rounded-[2rem] p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col items-center text-center"
                >
                  {/* Hover Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-${gradientFrom}/5 to-${gradientTo}/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                  <div className="relative z-10 flex flex-col items-center">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-${gradientFrom} to-${gradientTo} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-primary-600 transition-colors">
                      {benefit.title}
                    </h3>

                    <p className="text-slate-600 leading-relaxed text-lg font-medium">
                      {benefit.description}
                    </p>
                  </div>

                  {/* Decorative Blob */}
                  <div className={`absolute -bottom-10 -right-10 w-32 h-32 bg-${gradientFrom}/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500`} />
                </motion.div>
              )
            })}
          </div>

          {/* CTA Button */}
          <div className="mt-20 flex justify-center">
            <a href={benefitsSettings?.cta_link || 'https://aham.mbwhost.in/contact'} className="w-full flex justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[85%] sm:w-auto"
              >
                {benefitsSettings?.cta_text || 'Start Your Application'} <ChevronRightIcon className="h-5 w-5" />
              </motion.button>
            </a>
          </div>

        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section id="eligibility" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Soft Background Blobs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-primary-200/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">

            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-5 py-2 bg-primary-100 text-primary-700 rounded-full font-medium shadow-sm mb-6"
            >
              Loan Eligibility
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-extrabold text-gray-900 mt-6 mb-4 leading-tight tracking-tight"
            >
              {eligibilitySettings?.section_title || 'Eligibility Criteria'}
            </motion.h2>

            <p className="text-lg md:text-xl text-gray-600 mt-4 max-w-2xl mx-auto leading-relaxed">
              {eligibilitySettings?.section_subtitle || "Find out if you're eligible for Aham Composite Loan"}
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eligibility.map((item, index) => {
              // Icon mapping
              const iconMap = {
                UserIcon: User,
                GlobeIcon: Globe,
                BriefcaseIcon: Briefcase,
                DollarSign: DollarSign,
                DocumentTextIcon, // Imported from heroicons
                ShieldCheckIcon, // Imported from heroicons
                StarIcon: Star,
                ScaleIcon: Ruler, // Map Scale to Ruler
                CurrencyRupeeIcon, // Imported from heroicons
                User,
                Globe,
                Briefcase,
                Star,
                Shield
              };

              const IconComponent = iconMap[item.icon_name] || CheckCircle;
              const gradientFrom = item.gradient_from || 'blue-500';
              const gradientTo = item.gradient_to || 'cyan-400';

              return (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="
                group backdrop-blur-xl bg-white/70 
                border border-white/40 shadow-xl rounded-2xl 
                p-6 flex flex-col items-center text-center gap-5 
                hover:-translate-y-2 hover:shadow-2xl 
                transition-all duration-300
              "
                >
                  {/* Gradient Icon */}
                  <div
                    className={`
                  w-14 h-14 rounded-xl flex items-center justify-center 
                  bg-gradient-to-br from-${gradientFrom} to-${gradientTo}
                  text-white shadow-lg group-hover:scale-105 transition
                `}
                  >
                    <IconComponent className="h-7 w-7" />
                  </div>

                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold text-lg leading-snug">
                      {item.criteria}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>

          {/* CTA Button */}
          {/* <div className="text-center mt-16">
            <a href="/customer-360-eligibility-calculator">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="
              px-10 py-4 rounded-full text-white font-semibold 
             bg-[#ed2636]
              shadow-lg hover:bg-primary-600 transition
            "
              >
                Check Your Eligibility Now
              </motion.button>
            </a>
          </div> */}

        </div>
      </section>

      {/* Documents Required Section */}
      <section id="documents" className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">

        {/* Background blur circles */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-200/20 blur-3xl rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary-300/20 blur-3xl rounded-full"></div>

        <div className="container mx-auto px-6 relative z-10">

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-gray-900 mb-4">
              {documentsSettings?.section_title || 'Documents Required'}
            </h2>
            <p className="text-xl text-gray-600">
              {documentsSettings?.section_subtitle || 'Keep these documents ready for a smooth application process'}
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto">

            {/* Premium Tabs */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                { key: "common", label: "Common", icon: <FileText /> },
                { key: "salaried", label: "Salaried", icon: <Briefcase /> },
                { key: "selfEmployed", label: "Self-Employed", icon: <User /> },
                { key: "nri", label: "NRI", icon: <Globe /> },
                { key: "property", label: "Property", icon: <Home /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
              flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 backdrop-blur-xl
              border border-white/40 shadow-lg
              ${activeTab === tab.key
                      ? "bg-gradient-to-r from-primary-600 to-primary-500 text-white scale-105 shadow-primary/50"
                      : "bg-white/70 text-gray-700 hover:bg-white/90"
                    }
            `}
                >
                  <span className="text-lg">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content Card */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="backdrop-blur-xl bg-white/70 p-10 rounded-3xl border border-white/40 shadow-xl"
            >
              <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {documents[activeTab]?.map((doc, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="
                flex items-start gap-4 
                p-5 bg-gradient-to-br from-white to-gray-50 
                rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-200
              "
                  >
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-xl flex items-center justify-center shadow-inner">
                      <FileText className="h-6 w-6" />
                    </div>
                    <span className="text-gray-900 font-semibold">{doc}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

          </div>
        </div>
      </section>


      {/* Interest Rates Section */}
      <section
        id="interest"
        className="relative py-24 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white overflow-hidden"
      >
        {/* Decorative Blurs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl opacity-30"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Title */}
            <h2 className="text-5xl md:text-6xl font-extrabold mb-8 leading-tight drop-shadow-xl">
              {interestRatesSettings?.section_title || 'Affordable Home Loan'}
              <span className="block text-primary-200 mt-2">
                {interestRatesSettings?.section_subtitle || 'Interest Rates'}
              </span>
            </h2>

            {/* Description Section */}
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-10 md:p-14 shadow-2xl">
              <p className="text-xl text-white/90 leading-relaxed mb-6">
                {interestRatesSettings?.intro_text || 'Aham Housing Finance brings you transparent, competitive, and customer-friendly interest rates designed to make home construction affordable and stress-free.'}
              </p>

              <div className="grid md:grid-cols-3 gap-8 my-10">
                {interestRatesLoading ? (
                  // Loading skeletons
                  [1, 2, 3].map((i) => (
                    <div key={i} className="bg-white/10 border border-white/20 rounded-2xl p-6 h-48 animate-pulse"></div>
                  ))
                ) : (
                  interestRates.map((card, index) => (
                    <motion.div
                      key={card.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 + index * 0.2 }}
                      className="bg-white/10 border border-white/20 rounded-2xl p-6 backdrop-blur-lg shadow-lg hover:bg-white/20 transition-all flex flex-col items-center text-center"
                    >
                      <div className="text-3xl mb-3">{card.icon}</div>
                      <h4 className="text-xl font-semibold mb-2">{card.title}</h4>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Final Message */}
              <p className="text-lg text-white/85 mb-10 leading-relaxed">
                {interestRatesSettings?.closing_text || 'Begin your home construction journey with confidence and clarity. Aham ensures the process is smooth, transparent, and tailored to your needs.'}
              </p>

              {/* CTA Button */}
              {/* <motion.a
                href={interestRatesSettings?.cta_link || 'https://aham.mbwhost.in/emi-calculator'}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white-700 hover:text-black hover:bg-primary-50 px-10 py-4 rounded-xl font-semibold text-xl transition-all duration-300 shadow-xl inline-block"
              >
                {interestRatesSettings?.cta_text || 'Calculate EMI'}
              </motion.a> */}
            </div>
          </motion.div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {faqSettings?.section_title || 'Frequently Asked Questions'}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings?.section_subtitle || 'Find answers to common questions about Composite Loan'}
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqsLoading ? (
              // Loading Skeleton
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-16 animate-pulse"></div>
              ))
            ) : (
              faqs.map((faq, index) => (
                <motion.div
                  key={faq.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors duration-300"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-8">
                      {faq.question}
                    </span>
                    <span className={`transform transition-transform duration-300 ${activeFAQ === index ? 'rotate-180' : ''}`}>
                      <ChevronDownIcon className="w-6 h-6 text-primary-600" />
                    </span>
                  </button>

                  <div
                    className={`transition-all duration-300 ease-in-out ${activeFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="p-6 pt-0 text-gray-600 leading-relaxed border-t border-gray-100">
                      {faq.answer}
                    </div>
                  </div>
                </motion.div>
              )))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/construction-loan/home-construction-loan.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaSettings?.title || 'Ready to Start Building Your Dream Home?'}
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-white/90">
              {ctaSettings?.description || 'Take the first step towards constructing your perfect home with Aham\'s hassle-free loan process'}
            </p>
            <div className="flex justify-center">
              <motion.a
                href={ctaSettings?.button_link || 'https://aham.mbwhost.in/contact'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#ed2636] text-white hover:bg-white hover:text-primary-600 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[85%] sm:w-auto mx-auto"
              >
                {ctaSettings?.button_text || 'Apply Now'}
              </motion.a>
            </div>

          </motion.div>
        </div>
      </section>


    </div>
  );
};

export default HomeCompositeLoan;